import { castSecurity, castSecurityStatistics, castAccounts, castCurrency, castOrder } from '@deltix/cc-common';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil, map, filter, startWith, mergeMap, first } from 'rxjs/operators';
import { cloneDeepWith } from 'lodash';
import Big from 'big.js';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __classPrivateFieldGet(receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
}

const serializer = (value) => {
    if (value instanceof Big) {
        return value.toFixed();
    }
    else if (typeof value === 'function') {
        return null;
    }
    return;
};

var _WidgetApi_instances, _WidgetApi_destroy$, _WidgetApi_requests, _WidgetApi_events, _WidgetApi_send, _WidgetApi_call, _WidgetApi_process;
class WidgetApi {
    constructor(widgetId) {
        _WidgetApi_instances.add(this);
        this.widgetId = widgetId;
        _WidgetApi_destroy$.set(this, new Subject());
        _WidgetApi_requests.set(this, []);
        _WidgetApi_events.set(this, new Subject());
        fromEvent(window, 'message')
            .pipe(takeUntil(__classPrivateFieldGet(this, _WidgetApi_destroy$, "f")))
            .subscribe((message) => {
            __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_process).call(this, message);
        });
    }
    getSecurity(id) {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getSecurity', [id]).pipe(map(castSecurity));
    }
    getAccessToken() {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getAccessToken');
    }
    getSecurities() {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getSecurities').pipe(map((securities) => securities.map(castSecurity)));
    }
    getSecurityStatisticsByIds(...ids) {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getSecurityStatisticsByIds', ids).pipe(map(castSecurityStatistics));
    }
    getSecurityStatistics() {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getSecurityStatistics').pipe(map(castSecurityStatistics));
    }
    sendOrder(order) {
        __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_send).call(this, 'sendOrder', [order]);
    }
    getAccounts(inCurrency) {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getAccounts', [inCurrency]).pipe(map(castAccounts));
    }
    getReportingCurrency() {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getReportingCurrency');
    }
    getCurrencies() {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getCurrencies').pipe(map((currencies) => currencies.map(castCurrency)));
    }
    getCurrentTranslation() {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getCurrentTranslation');
    }
    getCurrentTheme() {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getCurrentTheme');
    }
    getUserCustomSettings() {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getUserCustomSettings');
    }
    get userCustomSettings$() {
        return __classPrivateFieldGet(this, _WidgetApi_events, "f").pipe(takeUntil(__classPrivateFieldGet(this, _WidgetApi_destroy$, "f")), filter(([action]) => action === '@USER_SETTINGS/RECEIVED_CUSTOM'), startWith(null), mergeMap(() => this.getUserCustomSettings()));
    }
    setUserCustomSetting(key, value, showSuccessAlert = true) {
        __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_send).call(this, 'setUserCustomSetting', [key, value, showSuccessAlert]);
    }
    getOrderViewState() {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getOrderViewState').pipe(map((orders) => orders.map(castOrder)));
    }
    getLocale() {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getLocale');
    }
    get locale$() {
        return __classPrivateFieldGet(this, _WidgetApi_events, "f").pipe(takeUntil(__classPrivateFieldGet(this, _WidgetApi_destroy$, "f")), filter(([action]) => action === '@I18N/SET_LANG'), startWith(null), mergeMap(() => this.getLocale()));
    }
    get orderViewState$() {
        return __classPrivateFieldGet(this, _WidgetApi_events, "f").pipe(takeUntil(__classPrivateFieldGet(this, _WidgetApi_destroy$, "f")), filter(([action]) => action === '@WIDGET/ORDERS_UPDATE'), startWith(null), mergeMap(() => this.getOrderViewState()));
    }
    getTradeRoomViewState() {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getTradeRoomViewState');
    }
    get tradeRoomViewState$() {
        return __classPrivateFieldGet(this, _WidgetApi_events, "f").pipe(takeUntil(__classPrivateFieldGet(this, _WidgetApi_destroy$, "f")), filter(([action]) => action === '@ORDER_VIEW/CHANGE'), startWith(null), mergeMap(() => this.getTradeRoomViewState()));
    }
    changeTradeRoomView(obj) {
        __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_send).call(this, 'changeTradeRoomView', [obj]);
    }
    changeAdvancedViewSettings(obj) {
        __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_send).call(this, 'changeAdvancedViewSettings', [obj]);
    }
    getAdvancedViewSettings() {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_call).call(this, 'getAdvancedViewSettings');
    }
    get advancedViewSettings$() {
        return __classPrivateFieldGet(this, _WidgetApi_events, "f").pipe(takeUntil(__classPrivateFieldGet(this, _WidgetApi_destroy$, "f")), filter(([action]) => action === '@WIDGET/GET_ADVANCED_VIEW_SETTINGS'), startWith(null), mergeMap(() => this.getAdvancedViewSettings()));
    }
    createTradingView(obj) {
        return __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_send).call(this, 'createTradingView', [obj]);
    }
    destroy() {
        __classPrivateFieldGet(this, _WidgetApi_destroy$, "f").next(true);
        __classPrivateFieldGet(this, _WidgetApi_destroy$, "f").complete();
        __classPrivateFieldGet(this, _WidgetApi_events, "f").complete();
    }
}
_WidgetApi_destroy$ = new WeakMap(), _WidgetApi_requests = new WeakMap(), _WidgetApi_events = new WeakMap(), _WidgetApi_instances = new WeakSet(), _WidgetApi_send = function _WidgetApi_send(action, args = []) {
    globalThis.window.parent.postMessage({
        action,
        args: cloneDeepWith(args, serializer),
        widgetId: this.widgetId,
    }, '*');
}, _WidgetApi_call = function _WidgetApi_call(action, args = []) {
    const subject$ = new Subject();
    __classPrivateFieldGet(this, _WidgetApi_requests, "f").push([subject$, action, ...args]);
    __classPrivateFieldGet(this, _WidgetApi_instances, "m", _WidgetApi_send).call(this, action, args);
    return subject$.pipe(first(), takeUntil(__classPrivateFieldGet(this, _WidgetApi_destroy$, "f")));
}, _WidgetApi_process = function _WidgetApi_process(message) {
    if (typeof message.data !== 'object' || !message.data) {
        return;
    }
    const { action, args = [], result, } = message.data;
    if (!action) {
        return;
    }
    let handled = false;
    for (let i = 0; i < __classPrivateFieldGet(this, _WidgetApi_requests, "f").length; i++) {
        const request = __classPrivateFieldGet(this, _WidgetApi_requests, "f")[i];
        if (request[1] !== action) {
            continue;
        }
        if (args.length !== request.length - 2) {
            continue;
        }
        let flag = true;
        for (let j = 0; j < args.length; j++) {
            flag = flag && args[j] === request[j + 2];
            if (!flag) {
                break;
            }
        }
        if (!flag) {
            continue;
        }
        request[0].next(result ? JSON.parse(result) : result);
        __classPrivateFieldGet(this, _WidgetApi_requests, "f").splice(i, 1);
        handled = true;
        break;
    }
    if (!handled) {
        __classPrivateFieldGet(this, _WidgetApi_events, "f").next([action, result]);
    }
};

const sendMessageToHost = (action, args, widgetId) => {
    const stream = fromEvent(window, 'message').pipe(filter((response) => typeof response.data === 'object' && response.data.action === action), map((response) => (response.data.result
        ? JSON.parse(response.data.result)
        : response.data.result)));
    window.parent.postMessage({
        action,
        args,
        widgetId,
    }, '*');
    return stream;
};

export { WidgetApi, sendMessageToHost };
